import React, { useState } from "react";
import {
  MapPin,
  Target,
  Clock3,
  Users,
  Database,
  Settings,
  ShieldCheck,
  CheckCircle2,
  UserCircle,
  Activity,
  Lock,
  Share2,
  KeyRound,
  Cookie,
} from "lucide-react";

const sections = [
  {
    title: "Data Collection",
    icon: UserCircle,
    items: [
      "Name, email address and phone number.",
      "Order and delivery details you provide when booking.",
      "Precise (GPS) location while the app is in use.",
      "Approximate location using Wi-Fi and mobile networks.",
      "Latitude and longitude of the delivery location you select from the map.",
      'Your current location only when you tap "Use Current Location" or open the map picker.',
    ],
  },
  {
    title: "Usage",
    icon: Activity,
    items: [
      "Process and track your orders.",
      "Send delivery updates and notifications.",
      "Offer farming advice tailored to you.",
      "Calculate delivery distance and charges.",
      "Generate the best delivery route.",
    ],
  },
  {
    title: "When We Collect It",
    icon: Clock3,
    items: [
      "Only while the bestseed app is open.",
      "Only after you grant location permission.",
      "Never when the app is running in the background.",
      "Never after the app has been closed.",
    ],
  },
  {
    title: "Protection",
    icon: Lock,
    items: [
      "SSL/TLS encryption on all data in transit.",
      "Strict access controls for internal systems.",
      "Regular security audits.",
    ],
  },
  {
    title: "Sharing",
    icon: Share2,
    items: [
      "Google Maps Platform for maps, address search and reverse geocoding.",
      "Firebase to securely store booking information.",
      "Assigned drivers and vendors to complete delivery.",
      "Payment partners to process transactions.",
    ],
    note: "We never sell or share your data with advertisers or unrelated third parties.",
  },
  {
    title: "Location Data",
    icon: MapPin,
    items: [
      "Stored with your booking history.",
      "Lets you review previous deliveries.",
      "Makes re-ordering easier.",
    ],
    note: "Deleting your account removes all associated location data within 30 days.",
    controls: true,
  },
  {
    title: "Your Rights",
    icon: KeyRound,
    items: [
      "Access your data at any time through your account.",
      "Update your details whenever they change.",
      "Delete your data or close your account whenever you choose.",
    ],
  },
  {
    title: "Cookies",
    icon: Cookie,
    items: [
      "Essential cookies keep the app working correctly.",
      "Analytics cookies help us understand how the app is used.",
      "Manage cookie preferences anytime via your browser settings.",
    ],
  },
];

export default function LocationDataPolicy() {
  const [activeIndex, setActiveIndex] = useState(0);
  const active = sections[activeIndex];
  const ActiveIcon = active.icon;

  return (
    <div style={{ minHeight: "100vh", background: "#f8fafc" }}>
      <section
        style={{
          background: "linear-gradient(135deg,#0076BE,#00a8ff)",
          color: "#fff",
          padding: "60px 20px",
          textAlign: "center",
        }}
      >
        <h1 style={{ fontSize: 42, marginBottom: 10 }}>Privacy Policy</h1>
        <p style={{ maxWidth: 700, margin: "0 auto", lineHeight: 1.8 }}>
          We collect only what we need to serve your orders, calculate
          deliveries and improve your booking experience.
        </p>

        <div
          style={{
            display: "flex",
            justifyContent: "center",
            gap: 12,
            flexWrap: "wrap",
            marginTop: 25,
          }}
        >
          {["GPS Enabled", "Permission Based", "Secure Delivery"].map((x) => (
            <span
              key={x}
              style={{
                background: "rgba(255,255,255,.18)",
                padding: "8px 18px",
                borderRadius: 30,
              }}
            >
              {x}
            </span>
          ))}
        </div>
      </section>

      <section style={{ maxWidth: 850, margin: "40px auto", padding: "0 20px" }}>
        {/* Tab bar */}
        <div
          style={{
            display: "flex",
            flexWrap: "wrap",
            gap: 8,
            marginBottom: 24,
            borderBottom: "1px solid #e5e7eb",
            paddingBottom: 12,
          }}
        >
          {sections.map((section, index) => {
            const TabIcon = section.icon;
            const isActive = activeIndex === index;
            return (
              <button
                key={section.title}
                onClick={() => setActiveIndex(index)}
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 8,
                  padding: "10px 16px",
                  borderRadius: 999,
                  border: isActive ? "1px solid #0076BE" : "1px solid #e5e7eb",
                  background: isActive ? "#0076BE" : "#fff",
                  color: isActive ? "#fff" : "#4b5563",
                  cursor: "pointer",
                  fontSize: 14,
                  fontWeight: 600,
                  transition: "background 0.15s ease, color 0.15s ease",
                }}
              >
                <TabIcon size={16} color={isActive ? "#fff" : "#0076BE"} />
                {section.title}
              </button>
            );
          })}
        </div>

        {/* Active tab content */}
        <div
          style={{
            background: "#fff",
            borderRadius: 18,
            padding: 28,
            borderLeft: "5px solid #0076BE",
            boxShadow: "0 4px 18px rgba(0,0,0,.06)",
          }}
        >
          <div style={{ display: "flex", alignItems: "center", gap: 14, marginBottom: 20 }}>
            <ActiveIcon color="#0076BE" size={28} />
            <h2 style={{ margin: 0, fontSize: 24 }}>{active.title}</h2>
          </div>

          <ul style={{ paddingLeft: 24, lineHeight: 2, color: "#4b5563", margin: 0 }}>
            {active.items.map((item) => (
              <li key={item} style={{ marginBottom: 8 }}>
                <CheckCircle2
                  size={16}
                  color="#0076BE"
                  style={{ marginRight: 8, verticalAlign: "middle" }}
                />
                {item}
              </li>
            ))}
          </ul>

          {active.note && (
            <div
              style={{
                background: "#E8F5FC",
                borderLeft: "4px solid #0076BE",
                padding: 16,
                borderRadius: 10,
                marginTop: 20,
                display: "flex",
                gap: 10,
                alignItems: "flex-start",
              }}
            >
              <ShieldCheck color="#0076BE" style={{ flexShrink: 0 }} />
              <span>{active.note}</span>
            </div>
          )}

          {active.controls && (
            <div style={{ marginTop: 20 }}>
              <div
                style={{
                  background: "#f8fafc",
                  padding: 18,
                  borderRadius: 10,
                  border: "1px solid #e5e7eb",
                  marginBottom: 16,
                }}
              >
                <strong>Android</strong>
                <br />
                Settings → Apps → bestseed → Permissions → Location → Deny
              </div>

              <div
                style={{
                  background: "#f8fafc",
                  padding: 18,
                  borderRadius: 10,
                  border: "1px solid #e5e7eb",
                  marginBottom: 16,
                }}
              >
                <strong>iPhone</strong>
                <br />
                Settings → bestseed → Location → Never
              </div>

              <div
                style={{
                  background: "#E8F5FC",
                  borderLeft: "4px solid #0076BE",
                  padding: 16,
                  borderRadius: 10,
                }}
              >
                Even if you deny permission, you can still create bookings by
                manually entering your delivery address.
              </div>
            </div>
          )}
        </div>

        <p style={{ textAlign: "center", color: "#94a3b8", marginTop: 30 }}>
          Last updated: April 2026. By using bestseed, you agree to this Privacy Policy.
        </p>
      </section>
    </div>
  );
}
